import React, { useState } from "react";
import {
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TableSortLabel,
    Button,
    Paper,
    Snackbar,
    Alert,
    Tooltip,
    Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import API_ENDPOINTS from "../../apis/endpoints";
import "./MovieDashboard.css";
import useSWR from "swr";
import SearchInput from "../../components/SearchInput";
import MoviesMetrics from "./MoviesMetrics";
import { Card } from "../../components/Card";

const MovieDashboard = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("movie");
    const [sortDirection, setSortDirection] = useState("asc");
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const navigate = useNavigate();

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;
    const { data, isLoading, error } = useSWR(
        `${API_ENDPOINTS.MOVIES}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    const movies = data?.results ?? [];
    const totalMovies = data?.meta?.total ?? 0;
    const summary = data?.summary ?? { amount: 0 };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setPage(0);
        Sentry.addBreadcrumb({
            category: "user",
            message: `User searched for: ${searchTerm}`,
            level: "info",
        });
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
        Sentry.addBreadcrumb({
            category: "user",
            message: `Sorting by ${column} in ${isAsc ? "desc" : "asc"} order`,
            level: "info",
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatAmount = (amount) => {
        return `₹${amount?.toFixed(2)} Cr`;
    };

    const renderCast = (cast) => {
        const castArray = cast?.split(", ");
        if (castArray?.length <= 3) return cast;
        return (
            <Tooltip title={cast} arrow>
                <span>{castArray?.slice(0, 3).join(", ")}...</span>
            </Tooltip>
        );
    };

    const formatRevenue = (revenue) => {
        if (revenue >= 10000000) {
            return `(₹${Math.ceil(revenue / 10000000)}Cr)`;
        } else if (revenue >= 100000) {
            return `(₹${Math.ceil(revenue / 100000)}L)`;
        } else {
            return `(₹${Math.ceil(revenue).toLocaleString()})`;
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    return (
        <Container maxWidth="lg">
            <Box className="movie-dashboard-container" sx={{ width: "100%" }}>
                <Typography variant="featureAccent" gutterBottom>
                    Movie Dashboard
                </Typography>
                <MoviesMetrics moviesData={movies} />

                <Box
                    sx={{
                        mt: 3,
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    {" "}
                    <Typography variant="highlightAccent" sx={{ ml: "0.2rem" }}>
                        All Movies
                    </Typography>
                    <SearchInput
                        onSearch={handleSearch}
                        placeHolder={"Movies..."}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        sx={{ maxWidth: "40rem" }}
                    />
                </Box>

                <Paper sx={{ width: "100%" }}>
                    <TableContainer
                        className="table-container"
                        sx={{ width: "100%", overflowX: "auto" }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {[
                                        "Movie",
                                        "Release Date",
                                        "Schedule",
                                        `Amount ${formatRevenue(
                                            summary.amount * 10000000
                                        )}`,
                                        "Genres",
                                        "Producers",
                                        "Writers",
                                        "Directors",
                                        "Banners",
                                        "Cast",
                                    ].map((column) => (
                                        <TableCell
                                            key={column}
                                            className="table-header-cell"
                                        >
                                            <TableSortLabel
                                                active={
                                                    sortColumn ===
                                                    column.toLowerCase()
                                                }
                                                direction={
                                                    sortColumn ===
                                                    column.toLowerCase()
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest(
                                                        column.toLowerCase()
                                                    )
                                                }
                                            >
                                                {column}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : movies?.length > 0 ? (
                                    movies.map((movie) => (
                                        <TableRow
                                            key={movie.id}
                                            className="table-row"
                                        >
                                            <TableCell className="table-cell">
                                                {movie.movie}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {movie.release}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {movie.schedule}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {formatAmount(movie.amount)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {movie.genres}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {renderCast(movie.producers)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {renderCast(movie.writers)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {renderCast(movie.directors)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {renderCast(movie.banners)}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {renderCast(movie.actors)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No movies found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalMovies}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        sx={{ width: "100%" }}
                    >
                        {error?.message
                            ? error?.message
                            : "An error occurred. Please try again later."}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

// Error boundary for monitoring component errors
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        Sentry.captureException(error);
    }

    render() {
        if (this.state.hasError) {
            return <h1>Something went wrong.</h1>;
        }

        return this.props.children;
    }
}

export default function MonitoredMovieDashboard() {
    return (
        <ErrorBoundary>
            <MovieDashboard />
        </ErrorBoundary>
    );
}
